import {
  Calendar,
  Download,
  LayoutGrid,
  List,
  Search,
  Settings,
  SlidersHorizontal,
  Users,
  ChevronDown,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { Input } from "../../../../components/ui/input";
import type {
  MeetingActionFilter,
  MeetingDateFilter,
  MeetingDisplayMode,
  MeetingViewFilter,
} from "../meetingsTypes";

type MeetingsHeaderProps = {
  activeView: MeetingViewFilter;
  onViewChange: (value: MeetingViewFilter) => void;
  displayMode: MeetingDisplayMode;
  onDisplayModeChange: (value: MeetingDisplayMode) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  dateFilter: MeetingDateFilter;
  onDateFilterChange: (value: MeetingDateFilter) => void;
  actionFilter: MeetingActionFilter;
  onActionFilterChange: (value: MeetingActionFilter) => void;
};

const viewOptions: Array<{ value: MeetingViewFilter; label: string; icon: typeof Calendar }> = [
  { value: "all", label: "All Meetings", icon: Calendar },
  { value: "my", label: "My Meetings", icon: Calendar },
  { value: "shared", label: "Shared Meetings", icon: Users },
];

const dateFilterOptions: Array<{ value: MeetingDateFilter; label: string }> = [
  { value: "all", label: "All dates" },
  { value: "upcoming", label: "Upcoming" },
  { value: "today", label: "Today" },
  { value: "past", label: "Past" },
];

const actionFilterOptions: Array<{ value: MeetingActionFilter; label: string }> = [
  { value: "all", label: "All actions" },
  { value: "needs_action", label: "Needs action" },
  { value: "followed_up", label: "Followed up" },
  { value: "none", label: "No action" },
];

const filterLabelMap: Record<MeetingDateFilter | MeetingActionFilter, string> = {
  all: "All",
  upcoming: "Upcoming",
  today: "Today",
  past: "Past",
  needs_action: "Needs action",
  followed_up: "Followed up",
  none: "No action",
};

const MeetingsHeader = ({
  activeView,
  onViewChange,
  displayMode,
  onDisplayModeChange,
  searchValue,
  onSearchChange,
  showFilters,
  onToggleFilters,
  dateFilter,
  onDateFilterChange,
  actionFilter,
  onActionFilterChange,
}: MeetingsHeaderProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const currentView = viewOptions.find((option) => option.value === activeView) || viewOptions[0];
  const CurrentViewIcon = currentView.icon;

  return (
    <div className="flex flex-col border-b border-border bg-background">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-lg font-semibold text-foreground">Meetings</h1>
        </div>
      </div>

      <div className="flex items-center gap-3 px-6 pb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleFilters}
          className={cn("gap-2 text-sm", showFilters && "bg-muted")}
        >
          <SlidersHorizontal className="h-4 w-4" />
          {showFilters ? "Hide Filters" : "Filters"}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2 text-sm">
              <CurrentViewIcon className="h-4 w-4" />
              {currentView.label}
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {viewOptions.map((option) => {
              const Icon = option.icon;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={() => onViewChange(option.value)}
                  className={cn(activeView === option.value && "bg-muted")}
                >
                  <Icon className="h-4 w-4" />
                  {option.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {showFilters && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 text-sm">
                  Date: {dateFilterOptions.find((item) => item.value === dateFilter)?.label || filterLabelMap[dateFilter]}
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44">
                {dateFilterOptions.map((option) => (
                  <DropdownMenuItem key={option.value} onSelect={() => onDateFilterChange(option.value)}>
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 text-sm">
                  Action: {actionFilterOptions.find((item) => item.value === actionFilter)?.label || filterLabelMap[actionFilter]}
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {actionFilterOptions.map((option) => (
                  <DropdownMenuItem key={option.value} onSelect={() => onActionFilterChange(option.value)}>
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        <div className="flex-1" />

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search meetings..."
            value={searchValue}
            onChange={(event) => onSearchChange(event.currentTarget.value)}
            className="h-8 w-64 pl-9 text-sm"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-sm"
          onClick={() => navigate(`/home/${params.workspaceId}/settings/meeting-automation`)}
        >
          <Settings className="h-4 w-4" />
          Configure
        </Button>

        <Button variant="outline" size="sm" className="gap-2 text-sm">
          <Download className="h-4 w-4" />
          Import
        </Button>

        <div className="flex items-center rounded-lg border border-border bg-muted p-0.5">
          <button
            type="button"
            onClick={() => onDisplayModeChange("list")}
            className={cn(
              "flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm font-medium transition-colors",
              displayMode === "list" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <List className="h-3.5 w-3.5" />
            List
          </button>
          <button
            type="button"
            onClick={() => onDisplayModeChange("board")}
            className={cn(
              "flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm font-medium transition-colors",
              displayMode === "board" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Board
          </button>
        </div>
      </div>
    </div>
  );
};

export default MeetingsHeader;
